/*
 * Copyright (c) 2020 Signum+ Software & Facility GmbH
 * Author: jahn@signum-plus.at
 */

import { ViewService } from '@angular-kit/view';
import { TimeRecordingViewFieldKey, TimeRecordingViewModel } from '../view-model/time-recording.view-model';
import {
  ActionModel,
  Alignments,
  ContainerModel,
  ContextBindings,
  FormFieldModel,
  ImageModel,
  KeyCodes,
  SelectInputModel,
  SelectInputOptions,
  TextInputModel,
  TextModel,
  ValueModel,
  ViewComponentKey
} from '@typescript-kit/view';
import { ChangedEvent, CoreValidationKey, hasValue, ObservableMap, toArray, validateRequired } from '@typescript-kit/core';
import { TimeRecordingViewKey } from '../key/time-recording-view.key';
import { WorkingHoursRecordFieldKey, WorkingHoursRecordModel } from '@teamworks/global';
import { TimeRecordingModelKey } from '@teamworks/global';
import { WeekDays } from '@teamworks/global';
import { AutoCompleteInputModel } from '../../shared/component-model/auto-complete-input.model';
import { SharedSvgKey } from '../../shared/key/shared-svg.key';
import { EmployeeTimeRecordingSharedViewService } from './employee-time-recording-shared.view-service';
import { TimeRecordingValidationKey } from '@teamworks/global';

export class EmployeeTimeRecordingWorkingHoursViewService extends ViewService<TimeRecordingViewModel, ContainerModel> {
  private weekdaySelectInputOptions: SelectInputOptions = {
    values: Object.values(WeekDays),
    formattedValues: Object.values(WeekDays).map((weekday) => `${TimeRecordingModelKey.WEEKDAY}/abbreviation/${weekday}`)
  };

  private readonly sharedViewService = this.injector.get(EmployeeTimeRecordingSharedViewService);

  private projectInputOptions: string[] = [];

  protected onViewModelPropertyChanged(event: ChangedEvent) {
    if (event.originalEvent !== event) {
      if (event.originalEvent.source === this.viewModel.defaultWorkingHoursRecordList) {
        this.onWorkingHoursRecordListPropertyChanged(event.originalEvent);
        this.refreshTotalWorkHoursModel(this.componentModel.items.get('totalWorkHours') as FormFieldModel);
      }
      return;
    }
    if (event.changes[TimeRecordingViewFieldKey.DEFAULT_WORKING_HOURS_RECORD_LIST]) {
      this.refreshComponentModel(this.componentModel);
    }
    if (event.changes[TimeRecordingViewFieldKey.PROJECT_LIST]) {
      this.projectInputOptions = this.viewModel.projectList
        .filter(project => project.isActive)
        .map(project => project.name);
      const recordListContainer = this.componentModel.items.get('recordList') as ContainerModel;
      recordListContainer.items.forEach((recordItemContainer: ContainerModel) => {
        const projectInput = recordItemContainer.items.get(WorkingHoursRecordFieldKey.PROJECT_ID) as AutoCompleteInputModel;
        projectInput.options = this.projectInputOptions;
      });
    }
  }

  protected onWorkingHoursRecordListPropertyChanged(event: ChangedEvent): void {
    if (event.originalEvent !== event) {
      this.onWorkingHoursRecordPropertyChanged(event.originalEvent);
      return;
    }
    this.refreshComponentModel(this.componentModel);
  }

  protected onWorkingHoursRecordPropertyChanged(event: ChangedEvent): void {
    if (event.changes[WorkingHoursRecordFieldKey.WEEKDAY] || event.changes[WorkingHoursRecordFieldKey.PROJECT_ID]) {
      // TODO: fix observable list sort
      // this.viewModel.defaultWorkingHoursRecordList.sort((a, b) => {
      // ...
      // });
      const workingHoursRecordModelList = this.viewModel.defaultWorkingHoursRecordList.toArray();
      this.viewModel.defaultWorkingHoursRecordList.setValues(
        workingHoursRecordModelList.sort((a, b) => {
          if (a.weekDay === b.weekDay) {
            if (a.projectId === b.projectId) {
              return 0;
            }
            if (!a.projectId) {
              return -1;
            }
            if (!b.projectId) {
              return 1;
            }
            return a.projectId.toLowerCase() <= b.projectId.toLowerCase() ? -1 : 1;
          }
          return a.weekDay < b.weekDay ? -1 : 1;
        })
      );
      const recordIndex = this.viewModel.defaultWorkingHoursRecordList.indexOf(event.source);
      const recordItemContainer = this.getRecordItemContainer(recordIndex);
      const inputModel = event.changes[WorkingHoursRecordFieldKey.WEEKDAY]
        ? recordItemContainer.items.get(WorkingHoursRecordFieldKey.WEEKDAY)
        : recordItemContainer.items.get(WorkingHoursRecordFieldKey.PROJECT_ID);
      inputModel.isFocused = true;
    }
    if (event.changes[WorkingHoursRecordFieldKey.HOURS] && !hasValue(event.changes[WorkingHoursRecordFieldKey.HOURS].currentValue)) {
      this.removeRecord(event.source);
    }
  }

  protected addRecord() {
    let newRecordIndex = this.viewModel.defaultWorkingHoursRecordList.firstIndex((record) => !hasValue(record.hours));
    if (newRecordIndex < 0) {
      newRecordIndex = this.viewModel.defaultWorkingHoursRecordList.size;
      const lastRecord = this.viewModel.defaultWorkingHoursRecordList.lastValue();
      this.viewModel.defaultWorkingHoursRecordList.push(
        new WorkingHoursRecordModel({
          employeeId: this.viewModel.employeeId,
          projectId: null,
          weekDay: lastRecord ? Math.min(lastRecord.weekDay + 1, WeekDays.SUNDAY) : WeekDays.MONDAY,
          hours: null,
        })
      );
    }
    const recordItemContainer = this.getRecordItemContainer(newRecordIndex);
    recordItemContainer.items.get(WorkingHoursRecordFieldKey.HOURS).isFocused = true;
  }

  protected removeRecord(record: WorkingHoursRecordModel) {
    const index = this.viewModel.defaultWorkingHoursRecordList.indexOf(record);
    this.viewModel.defaultWorkingHoursRecordList.remove(index);
    if (index > 0) {
      this.getRecordFieldInput(index - 1, WorkingHoursRecordFieldKey.HOURS).isFocused = true;
    } else if (this.viewModel.defaultWorkingHoursRecordList.size > 0) {
      this.getRecordFieldInput(0, WorkingHoursRecordFieldKey.HOURS).isFocused = true;
    }
  }

  protected createComponentModel(): ContainerModel {
    const model = new ContainerModel({
      tags: ['app-working-hours-container'],
      items: new ObservableMap({
        header: this.createHeaderContainer(),
        recordList: this.createWorkingHoursRecordListContainer(),
        totalWorkHours: this.createTotalWorkHoursModel()
      })
    });
    this.refreshComponentModel(model);
    return model;
  }

  protected refreshComponentModel(model: ContainerModel) {
    if (!this.viewModel.employeeId || !this.viewModel.defaultWorkingHoursRecordList) {
      model.isHidden = true;
      return;
    }
    model.isHidden = false;
    this.refreshWorkingHoursRecordListContainer(model.items.get('recordList') as ContainerModel);
    this.refreshTotalWorkHoursModel(model.items.get('totalWorkHours') as FormFieldModel);
  }

  private createHeaderContainer(): ContainerModel {
    return new ContainerModel({
      tags: ['app-working-hours-header'],
      items: [
        new TextModel({
          tags: ['app-expected-hours-title', 'app-title'],
          text: `${TimeRecordingViewKey.TIME_RECORDING_WORKING_HOURS}/expected-hours`
        }),
        new ActionModel({
          tags: ['app-accounting-list-header-action'],
          content: new ImageModel({
            type: ViewComponentKey.SVG_SYMBOL,
            source: SharedSvgKey.ADD_DOCUMENT
          }),
          onClick: () => {
            this.addRecord();
          }
        })
      ]
    });
  }

  private createWorkingHoursRecordListContainer(): ContainerModel {
    return new ContainerModel({
      tags: ['app-working-hours-record-list']
    });
  }

  private refreshWorkingHoursRecordListContainer(model: ContainerModel): void {
    model.items.setValues(toArray(this.viewModel.defaultWorkingHoursRecordList).map(
      (recordModel, index) => this.createWorkingHoursRecordItemContainer(recordModel, index)
    ), { exclusive: true });
  }

  private createWorkingHoursRecordItemContainer(record: WorkingHoursRecordModel, index: number): ContainerModel {
    const originalProject = record.projectId && this.viewModel.projectList.find(item => item.id === record.projectId) || null;
    const recordContainerModel = new ContainerModel({
      tags: ['app-working-hours-record-item'],
      items: [
        new SelectInputModel({
          key: WorkingHoursRecordFieldKey.WEEKDAY, tags: [`app-field-${WorkingHoursRecordFieldKey.WEEKDAY}`],
          options: this.weekdaySelectInputOptions.values,
          formattedOptions: this.weekdaySelectInputOptions.formattedValues,
          context: record,
          contextBinding: ContextBindings.TWO_WAY,
          onKeydown: (event: KeyboardEvent, model: ValueModel) => this.onInputKeydown(event, model, index),
        }),
        new AutoCompleteInputModel({
          key: WorkingHoursRecordFieldKey.PROJECT_ID, tags: [`app-field-${WorkingHoursRecordFieldKey.PROJECT_ID}`],
          options: this.projectInputOptions,
          value: originalProject ? originalProject.name : null,
          onValueChanged: async (projectName) => {
            if (!projectName) {
              record.projectId = null;
            } else {
              let project = this.viewModel.projectList.find(item => item.name === projectName);
              if (!project) {
                project = await this.sharedViewService.saveProject({ name: projectName, isConfirmed: false, isActive: true });
                await this.sharedViewService.refreshProjectList();
              }
              record.projectId = project.id;
            }
            this.validateRecordContainer(recordContainerModel);
          },
          context: record,
          placeholder: `${TimeRecordingViewKey.TIME_RECORDING_WORKING_HOURS}/no-project`,
          onKeydown: (event: KeyboardEvent, model: ValueModel) => this.onInputKeydown(event, model, index),
          validation: {
            [TimeRecordingValidationKey.TIME_RECORD_PROJECT]: { projectList: this.viewModel.projectList }
          },
        }),
        new TextInputModel({
          key: WorkingHoursRecordFieldKey.HOURS, tags: [`app-field-${WorkingHoursRecordFieldKey.HOURS}`],
          format: 'input-number-2', suffix: ' h', alignment: Alignments.RIGHT,
          context: record,
          contextBinding: ContextBindings.TWO_WAY,
          onKeydown: (event: KeyboardEvent, model: ValueModel) => this.onInputKeydown(event, model, index),
          validation: {
            [CoreValidationKey.REQUIRED]: (value, options, context) => !context.projectId || validateRequired(value)
          }
        })
      ]
    });
    this.validateRecordContainer(recordContainerModel);
    return recordContainerModel;
  }

  private validateRecordContainer(recordContainerModel: ContainerModel) {
    recordContainerModel.items.forEach((valueModel: ValueModel) => valueModel.validate());
  }

  private createTotalWorkHoursModel() {
    return new FormFieldModel({
      tags: ['app-total-work-hours'],
      label: `${TimeRecordingViewKey.TIME_RECORDING_WORKING_HOURS}/totalWorkHours`,
      control: new ValueModel({
        tags: ['app-form-value'],
        format: 'number-2', suffix: ' h', alignment: Alignments.RIGHT,
      })
    });
  }

  private refreshTotalWorkHoursModel(model: FormFieldModel) {
    const valueModel = model.control as ValueModel;
    if (this.viewModel.defaultWorkingHoursRecordList && this.viewModel.defaultWorkingHoursRecordList.size > 0) {
      model.isHidden = false;
      valueModel.value = this.calculateTotalWorkHours();
    } else {
      model.isHidden = true;
      valueModel.value = null;
    }
  }

  private calculateTotalWorkHours() {
    let result: number = null;
    this.viewModel.defaultWorkingHoursRecordList.forEach((record) => {
      if (hasValue(record.hours)) {
        result = (result || 0) + record.hours;
      }
    });
    return result;
  }

  private onInputKeydown(event: KeyboardEvent, model: ValueModel, index: number) {
    const inputElement = event.currentTarget;
    switch (event.key) {
      case KeyCodes.UP.key:
        this.getAboveRecordFieldInput(index, model.key).isFocused = true;
        event.preventDefault();
        break;
      case KeyCodes.DOWN.key:
        this.getBelowRecordFieldInput(index, model.key).isFocused = true;
        event.preventDefault();
        break;
      case KeyCodes.RIGHT.key:
        if (
          event.ctrlKey && event.altKey ||
          inputElement['selectionStart'] === undefined ||
          inputElement['selectionStart'] === inputElement['value'].length
        ) {
          this.getRightRecordFieldInput(index, model.key).isFocused = true;
          event.preventDefault();
        }
        break;
      case KeyCodes.LEFT.key:
        if (
          event.ctrlKey && event.altKey ||
          inputElement['selectionEnd'] === undefined ||
          inputElement['selectionEnd'] === 0
        ) {
          this.getLeftRecordFieldInput(index, model.key).isFocused = true;
          event.preventDefault();
        }
        break;
      case KeyCodes.ESC.key:
        const recordModel = model.context as WorkingHoursRecordModel;
        if (hasValue(recordModel.hours) || hasValue(recordModel.projectId)) {
          break;
        }
        if (model.key === WorkingHoursRecordFieldKey.HOURS && !inputElement['value']) {
          this.removeRecord(recordModel);
          event.preventDefault();
        }
        if (model.key === WorkingHoursRecordFieldKey.PROJECT_ID && !inputElement['value']) {
          this.removeRecord(recordModel);
          event.preventDefault();
        }
    }
  }

  private getAboveRecordFieldInput(index: number, key: string) {
    return this.getRecordFieldInput(index > 0 ? index - 1 : this.viewModel.defaultWorkingHoursRecordList.size - 1, key);
  }

  private getBelowRecordFieldInput(index: number, key: string) {
    return this.getRecordFieldInput(index < this.viewModel.defaultWorkingHoursRecordList.size - 1 ? index + 1 : 0, key);
  }

  private getLeftRecordFieldInput(index: number, key: string) {
    switch (key) {
      case WorkingHoursRecordFieldKey.WEEKDAY:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.HOURS);
      case WorkingHoursRecordFieldKey.PROJECT_ID:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.WEEKDAY);
      case WorkingHoursRecordFieldKey.HOURS:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.PROJECT_ID);
      default:
        throw new Error('Invalid key');
    }
  }

  private getRightRecordFieldInput(index: number, key: string) {
    switch (key) {
      case WorkingHoursRecordFieldKey.WEEKDAY:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.PROJECT_ID);
      case WorkingHoursRecordFieldKey.PROJECT_ID:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.HOURS);
      case WorkingHoursRecordFieldKey.HOURS:
        return this.getRecordFieldInput(index, WorkingHoursRecordFieldKey.WEEKDAY);
      default:
        throw new Error('Invalid key');
    }
  }

  private getRecordItemContainer(index: number): ContainerModel {
    return (this.componentModel.items.get('recordList') as ContainerModel).items.get(index.toString()) as ContainerModel;
  }

  private getRecordFieldInput(index: number, key: string): ValueModel {
    return this.getRecordItemContainer(index).items.get(key) as ValueModel;
  }


}
